import React, { useState } from "react";
import { Mail, MapPin, MessageCircle, Instagram, Globe, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Logo } from "../brand/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactFooter = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
      });
      toast.success(
        data?.email_sent
          ? "Thanks — we’ll reply within 24 hours."
          : "Thanks — your message was saved. We’ll reply shortly.",
        { description: `Reference #${(data?.id || "").slice(0, 8)}` },
      );
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again or email us directly.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer
      id="contact"
      data-testid="contact-footer-section"
      className="bg-ink text-bone pt-24 md:pt-32 pb-10"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Contact form */}
          <div className="lg:col-span-7">
            <div className="overline text-terracotta mb-5">Reach Us</div>
            <h2 className="font-display font-black text-4xl md:text-6xl tracking-tight leading-[1.02]">
              Stock a product?
              <br />
              <span className="font-wordmark italic font-semibold text-bone/90">
                Send a question?
              </span>
            </h2>
            <p className="mt-5 text-bone/65 max-w-lg leading-relaxed">
              Wholesale enquiries, custom orders, suggestions for products we
              should source — all welcome.
            </p>

            <form
              onSubmit={onSubmit}
              noValidate
              data-testid="contact-form"
              className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5"
            >
              <div className="md:col-span-1">
                <label htmlFor="name" className="overline text-bone/60 block mb-2">
                  Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={onChange}
                  data-testid="contact-input-name"
                  className="w-full bg-transparent border-b border-bone/30 focus:border-terracotta outline-none py-3 text-bone placeholder:text-bone/40 font-body text-base transition-colors"
                  placeholder="Your name"
                />
              </div>
              <div className="md:col-span-1">
                <label htmlFor="email" className="overline text-bone/60 block mb-2">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  data-testid="contact-input-email"
                  className="w-full bg-transparent border-b border-bone/30 focus:border-terracotta outline-none py-3 text-bone placeholder:text-bone/40 font-body text-base transition-colors"
                  placeholder="you@domain.com"
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="message" className="overline text-bone/60 block mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={form.message}
                  onChange={onChange}
                  data-testid="contact-input-message"
                  className="w-full bg-transparent border-b border-bone/30 focus:border-terracotta outline-none py-3 text-bone placeholder:text-bone/40 font-body text-base resize-none transition-colors"
                  placeholder="Tell us what you need."
                />
              </div>
              <div className="md:col-span-2 mt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="group inline-flex items-center gap-3 bg-terracotta hover:bg-bone hover:text-ink transition-colors duration-300 text-white px-7 py-4 rounded-sm font-display font-semibold text-sm tracking-wide disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send Message"}
                  <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </form>
          </div>

          {/* Brand info / contact details */}
          <aside className="lg:col-span-5 lg:pl-8 lg:border-l lg:border-bone/15">
            <div className="bg-bone/[0.04] border border-bone/15 p-6 md:p-8 rounded-sm">
              <Logo variant="compact" tone="light" />
              <p className="mt-6 text-bone/70 text-sm leading-relaxed">
                Niche nutrition for active lifestyles, sourced for the UAE.
                Two products today. A considered pantry tomorrow.
              </p>
            </div>

            <div className="mt-8 space-y-5">
              <div className="flex items-start gap-4" data-testid="contact-location">
                <MapPin className="w-4 h-4 text-terracotta mt-1 shrink-0" />
                <div>
                  <div className="overline text-bone/55">Based In</div>
                  <div className="font-display font-semibold text-bone text-base mt-1">
                    Dubai, United Arab Emirates
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="contact-email">
                <Mail className="w-4 h-4 text-terracotta mt-1 shrink-0" />
                <div>
                  <div className="overline text-bone/55">Email</div>
                  <a
                    href="mailto:gareth@thrivebymadsen.com"
                    className="font-display font-semibold text-bone hover:text-terracotta text-base mt-1 inline-block link-underline"
                  >
                    gareth@thrivebymadsen.com
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="contact-whatsapp">
                <MessageCircle className="w-4 h-4 text-terracotta mt-1 shrink-0" />
                <div>
                  <div className="overline text-bone/55">WhatsApp</div>
                  <a
                    href="https://wa.me/971501227625"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-display font-semibold text-bone hover:text-terracotta text-base mt-1 inline-block link-underline"
                  >
                    +971 50 122 7625
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="contact-instagram">
                <Instagram className="w-4 h-4 text-terracotta mt-1 shrink-0" />
                <div>
                  <div className="overline text-bone/55">Instagram</div>
                  <a
                    href="https://www.instagram.com/thrive.bymadsen/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-display font-semibold text-bone hover:text-terracotta text-base mt-1 inline-block link-underline"
                  >
                    @thrive.bymadsen
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="contact-website">
                <Globe className="w-4 h-4 text-terracotta mt-1 shrink-0" />
                <div>
                  <div className="overline text-bone/55">Website</div>
                  <a
                    href="https://www.thrivebymadsen.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-display font-semibold text-bone hover:text-terracotta text-base mt-1 inline-block link-underline"
                  >
                    www.thrivebymadsen.com
                  </a>
                </div>
              </div>
            </div>
          </aside>
        </div>

        <div className="mt-24 md:mt-32 border-t border-bone/15 pt-10">
          <div
            className="font-display uppercase text-bone leading-[0.85] text-[clamp(4rem,16vw,14rem)] font-black tracking-[-0.02em] select-none"
            aria-hidden="true"
            data-testid="footer-wordmark"
          >
            THRIVE<span className="text-terracotta">.</span>
          </div>
          <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <p className="overline text-bone/55" data-testid="footer-copyright">
              © {new Date().getFullYear()} Thrive by Madsen · All rights reserved
            </p>
            <p className="overline text-bone/55">
              Curated in the UAE · Sourced globally
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
